import {
  shouldShowLimitWarning,
  formatLimitWarningMessage,
} from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { motion } from 'motion/react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { XStack } from '@/components/ui/layout/layout';
import { researchQuotaLabel } from '@/features/chat/chat.hooks';
import { useHashRoute } from '@/hooks/useHashRoute';
import { trackEvent } from '@/utils/analytics';
import { classList } from '@/utils/cn';

import { ReferralCta } from './referral-cta';
import { useSubscription } from './subscription.query';
import { subscriptionStore } from './subscription.store';

const ANALYTICS_SOURCE = 'research_limit_warning';

interface ResearchLimitWarningProps {
  isNewThread?: boolean;
  className?: string;
}

/** Credits govern regular chat; only Max mode is still metered by message count. */
export const ResearchLimitWarning = observer(
  ({ isNewThread, className }: ResearchLimitWarningProps) => {
    const { status } = useSubscription();
    const { setHash } = useHashRoute();

    const limits = status.research_limits;
    const remaining = limits?.messages_remaining;

    const handleUpgrade = () => {
      trackEvent('UPGRADE_BUTTON_CLICKED', { source: ANALYTICS_SOURCE });
      setHash('settings/Subscription');
    };

    if (
      !shouldShowLimitWarning(limits, !!isNewThread) ||
      remaining === undefined
    )
      return null;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <XStack
          className={classList(
            'items-center justify-between gap-3',
            'bg-blue-50/20 dark:bg-blue-950/20',
            'rounded-t-xl',
            'p-1.5',
            'border-b dark:border-border-strong border-border',
            className
          )}
        >
          <XStack className="items-center gap-2 px-2">
            <Icons.warnTriangle className="size-4 text-foreground-muted" />
            <span className="text-sm text-foreground-subtle">
              {formatLimitWarningMessage(remaining, researchQuotaLabel())}
            </span>
          </XStack>
          <XStack className="items-center gap-2 shrink-0">
            {subscriptionStore.canUpgrade && (
              <Button size="sm" onClick={handleUpgrade}>
                Upgrade
              </Button>
            )}
            <ReferralCta source={ANALYTICS_SOURCE} />
          </XStack>
        </XStack>
      </motion.div>
    );
  }
);
